// apps/web/src/pages/estoque/rateio-diario-aba.tsx — Fase 13, Plano 32 (EST-15, D-02, D-03).
// Aba "Rateio diário" da tela de Consumo de tinta: uma linha por dia x cor de alto giro (sem lote, sem devolução). O
// consumo do dia só chega ao rascunho fiscal depois de fechado; dia aberto anterior a hoje fica como "Rateio pendente".
// Expandir mostra a distribuição entre lotes (pela área pintada). Fechar exige estoque:movimentar (o servidor repete a
// checagem). Soma, área e distribuição são do servidor (13-28); esta aba só apresenta.
import { Fragment, useCallback, useEffect, useState, type MouseEvent } from 'react';
import { CalendarClock, ChevronDown, ChevronRight, RefreshCw, Ruler } from 'lucide-react';
import type { DistribuicaoRateio, ListaRateios, RateioDiaLinha } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { TablePagination } from '../../components/ui/table-pagination';
import { apiMutate } from '../../lib/csrf-fetch';
import { TEXTO_RATEIO_PENDENTE, estadoRateio, mensagemErroRateio, podeFecharRateio } from '../../lib/estoque/consumo-tinta';
import { formatarQuantidadeEstoque } from '../../lib/estoque/formatacao';
import { formatarData, hojeIso } from '../../lib/vendas/formatacao';
import { FecharRateioDialog, type AlvoFechamentoRateio } from './fechar-rateio-dialog';

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const ROTULO = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const TAMANHO_PAGINA_RATEIO = 50;
/** Apoio do badge "Rateio pendente" (também usado na linha expandida do relatório). */
export const APOIO_RATEIO_PENDENTE = 'O consumo deste dia ainda não chegou ao rascunho fiscal.';
const MATERIAIS_URL = '/cadastros/materiais';

function chaveLinha(linha: RateioDiaLinha): string {
  return `${linha.dia}-${linha.materialId}`;
}

function corDaLinha(linha: RateioDiaLinha): string {
  return linha.corCodigo ?? linha.tintaCodigo;
}

/** "Distribuição entre lotes" do dia: prévia enquanto aberto, gravada quando fechado. */
function DistribuicaoEntreLotes({ linha }: { linha: RateioDiaLinha }) {
  const [dados, setDados] = useState<DistribuicaoRateio | null>(null);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setErro(false);
      try {
        const params = new URLSearchParams({ dia: linha.dia, materialId: linha.materialId });
        const res = await fetch(`/api/v1/estoque/tinta/rateios/detalhe?${params.toString()}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as DistribuicaoRateio;
        if (!sinal || sinal.vivo) setDados(json);
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      }
    },
    [linha.dia, linha.materialId],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  if (linha.entregueKg === 0) {
    return <p className="text-sm text-muted-foreground">Nenhuma tinta entregue nesta cor neste dia.</p>;
  }

  return (
    <section aria-label="Distribuição entre lotes" className="flex flex-col gap-2">
      <h3 className={ROTULO}>Distribuição entre lotes</h3>
      {erro ? (
        <div role="alert" className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-rose-700">Não foi possível carregar a distribuição deste dia.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void carregar()}>
            <RefreshCw aria-hidden="true" />
            Tentar novamente
          </Button>
        </div>
      ) : dados === null ? (
        <Skeleton className="h-8 w-full max-w-xl" />
      ) : dados.lotes.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum lote pintado com esta cor neste dia.</p>
      ) : (
        <>
          <table className="w-full max-w-3xl text-sm">
            <caption className="sr-only">
              Distribuição de {corDaLinha(linha)} entre os lotes do dia {formatarData(linha.dia)}
            </caption>
            <thead className="text-left">
              <tr>
                <th scope="col" className={`${ROTULO} py-1 pr-3`}>Lote</th>
                <th scope="col" className={`${ROTULO} py-1 pr-3 text-right`}>Peças</th>
                <th scope="col" className={`${ROTULO} py-1 pr-3 text-right`}>Área (m²)</th>
                <th scope="col" className={`${ROTULO} py-1 pr-3 text-right`}>Consumo rateado (kg)</th>
              </tr>
            </thead>
            <tbody>
              {dados.lotes.map((lote) => (
                <tr key={lote.loteId} className="border-t align-top">
                  <td className="py-1 pr-3">
                    <span className="font-semibold tabular-nums">{lote.loteInternoPieltec}</span>
                    {lote.opNumero && <span className="text-muted-foreground"> · OP {lote.opNumero}</span>}
                    {lote.semArea && (
                      <div className="mt-1 inline-flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700" role="alert">
                        <Ruler className="size-3 shrink-0" aria-hidden="true" />
                        <span>Sem área unitária cadastrada</span>
                        <a href={MATERIAIS_URL} className="underline underline-offset-2">
                          Abrir cadastro do material
                        </a>
                      </div>
                    )}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">{formatarQuantidadeEstoque(lote.pecas, 'un')}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{formatarQuantidadeEstoque(lote.areaM2, '')}</td>
                  <td className="py-1 pr-3 text-right font-semibold tabular-nums">{formatarQuantidadeEstoque(lote.consumoKg, '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {dados.lotes.some((lote) => lote.semArea) && (
            <p className="text-sm text-rose-700">Os lotes sem área unitária ficam fora do rateio até o cadastro do material ser corrigido.</p>
          )}
          {dados.kgPorM2 !== null && (
            <p className="text-sm text-muted-foreground tabular-nums">
              {formatarQuantidadeEstoque(dados.entregueKg, 'kg')} entregues ÷ {formatarQuantidadeEstoque(dados.areaTotalM2, 'm²')} = {formatarQuantidadeEstoque(dados.kgPorM2, 'kg/m²')}
            </p>
          )}
        </>
      )}
    </section>
  );
}

interface LinhaRateioProps {
  readonly linha: RateioDiaLinha;
  readonly hoje: string;
  readonly aberta: boolean;
  readonly podeMovimentar: boolean;
  readonly onAlternar: () => void;
  readonly onFechar: (linha: RateioDiaLinha) => void;
}

function LinhaRateio({ linha, hoje, aberta, podeMovimentar, onAlternar, onFechar }: LinhaRateioProps) {
  const idDetalhe = `rateio-${chaveLinha(linha)}-detalhe`;
  const estado = estadoRateio(linha, hoje);
  const pendente = estado === 'pendente';
  const cor = corDaLinha(linha);

  function cliqueNaLinha(event: MouseEvent<HTMLTableRowElement>): void {
    if ((event.target as HTMLElement).closest('a,button,[role="menuitem"]')) return;
    onAlternar();
  }

  return (
    <Fragment>
      <tr onClick={cliqueNaLinha} className="cursor-pointer border-t hover:bg-slate-50">
        <td className="w-12 px-1 py-1">
          <button
            type="button"
            onClick={onAlternar}
            aria-expanded={aberta}
            aria-controls={idDetalhe}
            aria-label={`${aberta ? 'Recolher' : 'Expandir'} rateio de ${cor} em ${formatarData(linha.dia)}`}
            className="inline-flex size-11 items-center justify-center rounded-md hover:bg-slate-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            {aberta ? <ChevronDown className="size-4" aria-hidden="true" /> : <ChevronRight className="size-4" aria-hidden="true" />}
          </button>
        </td>
        <td className="whitespace-nowrap px-3 py-2 font-semibold tabular-nums">{formatarData(linha.dia)}</td>
        <td className="px-3 py-2">
          <div className="font-semibold">{cor}</div>
          {linha.corCodigo && <div className="text-muted-foreground tabular-nums">{linha.tintaCodigo}</div>}
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums">{formatarQuantidadeEstoque(linha.entregueKg, 'kg')}</td>
        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{linha.lotesPintados}</td>
        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatarQuantidadeEstoque(linha.areaTotalM2, '')}</td>
        <td className="px-3 py-2">
          <div className="flex flex-col items-start gap-1">
            <span className="text-sm font-semibold">{linha.situacao === 'FECHADO' ? 'Fechado' : 'Aberto'}</span>
            {pendente && (
              <>
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                  <CalendarClock className="size-3" aria-hidden="true" />
                  {TEXTO_RATEIO_PENDENTE}
                </span>
                <span className="text-xs text-amber-700">{APOIO_RATEIO_PENDENTE}</span>
              </>
            )}
          </div>
        </td>
        <td className="px-3 py-2 text-right">
          {podeMovimentar && podeFecharRateio(linha, hoje) && (
            <Button type="button" variant="secondary" size="sm" className="min-h-11 sm:min-h-8" onClick={() => onFechar(linha)}>
              Fechar rateio do dia
            </Button>
          )}
        </td>
      </tr>
      {aberta && (
        <tr id={idDetalhe} className="border-t bg-muted/50">
          <td colSpan={8} className="px-4 py-3">
            <DistribuicaoEntreLotes linha={linha} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export interface RateioDiarioAbaProps {
  /** Só com estoque:movimentar o botão "Fechar rateio do dia" é renderizado (a autorização real é do servidor). */
  readonly podeMovimentar: boolean;
}

export function RateioDiarioAba({ podeMovimentar }: RateioDiarioAbaProps) {
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(TAMANHO_PAGINA_RATEIO);
  const [dados, setDados] = useState<ListaRateios | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(new Set());
  const [alvo, setAlvo] = useState<AlvoFechamentoRateio | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erroFechamento, setErroFechamento] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const hoje = hojeIso();

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const params = new URLSearchParams({ pagina: String(pagina), tamanhoPagina: String(tamanhoPagina) });
        const res = await fetch(`/api/v1/estoque/tinta/rateios?${params.toString()}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ListaRateios;
        if (!sinal || sinal.vivo) {
          setDados(json);
          setAbertas(new Set());
        }
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [pagina, tamanhoPagina],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  function alternar(chave: string): void {
    setAbertas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  }

  function abrirFechamento(linha: RateioDiaLinha): void {
    setErroFechamento(null);
    setSucesso(null);
    setAlvo({ dia: linha.dia, materialId: linha.materialId, cor: corDaLinha(linha) });
  }

  async function confirmarFechamento(): Promise<void> {
    if (!alvo || enviando) return;
    setEnviando(true);
    setErroFechamento(null);
    try {
      const res = await apiMutate('/api/v1/estoque/tinta/rateios/fechar', { body: { dia: alvo.dia, materialId: alvo.materialId } });
      if (!res.ok) {
        let corpo: unknown = null;
        try {
          corpo = await res.json();
        } catch {
          corpo = null;
        }
        setErroFechamento(mensagemErroRateio(res.status, corpo));
        return;
      }
      setSucesso(`Rateio de ${alvo.cor} em ${formatarData(alvo.dia)} fechado. O consumo já pode chegar ao rascunho fiscal.`);
      setAlvo(null);
      await carregar();
    } catch {
      setErroFechamento(mensagemErroRateio(0, null));
    } finally {
      setEnviando(false);
    }
  }

  const itens = dados?.itens ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div role="status" aria-live="polite" className={sucesso ? 'rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700' : 'sr-only'}>
        {sucesso}
      </div>

      {erro ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar o rateio diário. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : carregando ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando rateio diário">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : itens.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhuma cor de alto giro cadastrada</h2>
            <p className="max-w-xl text-sm text-muted-foreground">
              Marque &quot;Alto giro, sem devolução&quot; no cadastro da tinta (ex.: cinza e preto) para ativar o rateio.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden py-0 gap-0">
          <table className="w-full text-sm">
            <caption className="sr-only">Rateio diário do consumo das cores de alto giro</caption>
            <thead className="sticky top-0 z-10 bg-slate-100 text-left">
              <tr>
                <th scope="col" className="w-12 px-1 py-2">
                  <span className="sr-only">Expandir</span>
                </th>
                <th scope="col" className={CABECALHO}>Dia</th>
                <th scope="col" className={CABECALHO}>Cor</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Entregue no dia</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Lotes pintados</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Área total (m²)</th>
                <th scope="col" className={CABECALHO}>Situação</th>
                <th scope="col" className="px-3 py-2">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {itens.map((linha) => (
                <LinhaRateio
                  key={chaveLinha(linha)}
                  linha={linha}
                  hoje={hoje}
                  aberta={abertas.has(chaveLinha(linha))}
                  podeMovimentar={podeMovimentar}
                  onAlternar={() => alternar(chaveLinha(linha))}
                  onFechar={abrirFechamento}
                />
              ))}
            </tbody>
          </table>
          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            totalItems={dados?.total ?? 0}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
            itemLabel="dias"
          />
        </Card>
      )}

      {podeMovimentar && (
        <FecharRateioDialog
          alvo={alvo}
          enviando={enviando}
          erro={erroFechamento}
          aoConfirmar={() => void confirmarFechamento()}
          aoFechar={() => setAlvo(null)}
        />
      )}
    </div>
  );
}
